/**
 * Structured logging utility with performance metrics
 * ✅ Phase 1 Fix: Environment-based log level control
 * Replaces console.* calls with proper logging levels and contexts
 */

import { captureException, addBreadcrumb } from './sentry';

const isDevelopment = import.meta.env.MODE === 'development';
const LOG_LEVEL = import.meta.env.VITE_LOG_LEVEL || (isDevelopment ? 'debug' : 'warn');

export type LogLevel = 'DEBUG' | 'INFO' | 'WARN' | 'ERROR';
export type LogContext = 'SPECS' | 'DATABASE' | 'UI' | 'PERFORMANCE' | 'UPLOAD' | 'AUTH';

interface LogEntry {
  level: LogLevel;
  context: LogContext;
  message: string;
  metadata?: Record<string, unknown>;
  timestamp: string;
  performance?: {
    duration?: number;
    operation?: string;
  };
}

class StructuredLogger {
  private isDevelopment = import.meta.env.MODE === 'development';
  private performanceMarks = new Map<string, number>();

  private log(level: LogLevel, context: LogContext, message: string, metadata?: Record<string, unknown>, performance?: LogEntry['performance']) {
    const entry: LogEntry = {
      level,
      context,
      message,
      metadata,
      timestamp: new Date().toISOString(),
      performance
    };

    // Always log errors and warnings
    const shouldLog = this.isDevelopment || level === 'ERROR' || level === 'WARN';

    if (shouldLog) {
      const prefix = `[${context}:${level}]`;
      const logMessage = `${prefix} ${message}`;
      
      switch (level) {
        case 'ERROR':
          console.error(logMessage, { ...metadata, performance });
          break;
        case 'WARN':
          console.warn(logMessage, { ...metadata, performance });
          break;
        case 'INFO':
          console.info(logMessage, { ...metadata, performance });
          break;
        case 'DEBUG':
          console.debug(logMessage, { ...metadata, performance });
          break;
      }
    }

    // Store metrics for performance monitoring
    if (performance?.duration) {
      this.recordPerformanceMetric(context, performance.operation || 'unknown', performance.duration);
    }
  }

  // Performance timing utilities
  startPerformanceTimer(operation: string): () => number {
    const startTime = performance.now();
    const markKey = `${operation}_${Date.now()}`;
    this.performanceMarks.set(markKey, startTime);
    
    return () => {
      const endTime = performance.now();
      const duration = endTime - startTime;
      this.performanceMarks.delete(markKey);
      return duration;
    };
  }

  private recordPerformanceMetric(context: LogContext, operation: string, duration: number) {
    if (duration > 1000) { // Slow operation threshold
      this.warn(context, `Slow operation detected: ${operation}`, { duration: `${duration.toFixed(2)}ms` });
    }
  }

  // Public logging methods
  debug(context: LogContext, message: string, metadata?: Record<string, unknown>) {
    this.log('DEBUG', context, message, metadata);
  }

  info(context: LogContext, message: string, metadata?: Record<string, unknown>, performance?: LogEntry['performance']) {
    this.log('INFO', context, message, metadata, performance);
  }

  warn(context: LogContext, message: string, metadata?: Record<string, unknown>) {
    this.log('WARN', context, message, metadata);
  }

  error(context: LogContext, message: string, error?: Error | unknown, metadata?: Record<string, unknown>) {
    const errorMetadata = {
      ...metadata,
      error: error instanceof Error ? {
        name: error.name,
        message: error.message,
        stack: error.stack
      } : error
    };
    this.log('ERROR', context, message, errorMetadata);
    
    // Send to Sentry if available
    if (error instanceof Error) {
      captureException(error, {
        tags: { context },
        extra: { message, ...metadata },
        level: 'error',
      });
    }
    
    // Add breadcrumb for debugging
    addBreadcrumb(message, context, errorMetadata);
  }

  // Performance logging with automatic timing
  async withPerformanceLogging<T>(
    context: LogContext, 
    operation: string, 
    fn: () => Promise<T>
  ): Promise<T> {
    const endTimer = this.startPerformanceTimer(operation);
    
    try {
      this.debug(context, `Starting ${operation}`);
      const result = await fn();
      const duration = endTimer();
      this.info(context, `Completed ${operation}`, undefined, { duration, operation });
      return result;
    } catch (error) {
      const duration = endTimer();
      this.error(context, `Failed ${operation}`, error, { duration, operation });
      throw error;
    }
  }

  // Get performance summary
  getPerformanceReport(): Record<string, unknown> {
    return {
      activeTimers: this.performanceMarks.size,
      timestamp: new Date().toISOString()
    };
  }
}

export const logger = new StructuredLogger();

// ✅ Phase 1 Fix: Convenience exports with log level control
export const specsLogger = {
  debug: (message: string, metadata?: Record<string, unknown>) => {
    if (['debug'].includes(LOG_LEVEL)) {
      logger.debug('SPECS', message, metadata);
    }
  },
  info: (message: string, metadata?: Record<string, unknown>) => {
    if (['debug', 'info'].includes(LOG_LEVEL)) {
      logger.info('SPECS', message, metadata);
    }
  },
  warn: (message: string, metadata?: Record<string, unknown>) => {
    if (['debug', 'info', 'warn'].includes(LOG_LEVEL)) {
      logger.warn('SPECS', message, metadata);
    }
  },
  error: (message: string, error?: unknown, metadata?: Record<string, unknown>) => {
    // Always log errors
    logger.error('SPECS', message, error, metadata);
  },
  withPerformance: <T>(operation: string, fn: () => Promise<T>) => logger.withPerformanceLogging('SPECS', operation, fn),
  logLayerSwitch: (layerId: string, cached: boolean, duration: number) => {
    if (['debug', 'info'].includes(LOG_LEVEL)) {
      logger.info('SPECS', `Layer switch ${cached ? '⚡ cached' : '🔄 loading'}`, {
        layerId,
        cached,
        duration: `${duration.toFixed(0)}ms`,
        performance: duration < 100 ? 'excellent' : duration < 500 ? 'good' : 'slow'
      });
    }
  }
};

export const uploadLogger = {
  debug: (message: string, metadata?: Record<string, unknown>) => logger.debug('UPLOAD', message, metadata),
  info: (message: string, metadata?: Record<string, unknown>) => logger.info('UPLOAD', message, metadata),
  warn: (message: string, metadata?: Record<string, unknown>) => logger.warn('UPLOAD', message, metadata),
  error: (message: string, error?: unknown, metadata?: Record<string, unknown>) => logger.error('UPLOAD', message, error, metadata),
};

export const dbLogger = {
  debug: (message: string, metadata?: Record<string, unknown>) => logger.debug('DATABASE', message, metadata),
  info: (message: string, metadata?: Record<string, unknown>) => logger.info('DATABASE', message, metadata),
  warn: (message: string, metadata?: Record<string, unknown>) => logger.warn('DATABASE', message, metadata),
  error: (message: string, error?: unknown, metadata?: Record<string, unknown>) => logger.error('DATABASE', message, error, metadata),
  withPerformance: <T>(operation: string, fn: () => Promise<T>) => logger.withPerformanceLogging('DATABASE', operation, fn)
};

export const uiLogger = {
  debug: (message: string, metadata?: Record<string, unknown>) => logger.debug('UI', message, metadata),
  info: (message: string, metadata?: Record<string, unknown>) => logger.info('UI', message, metadata),
  warn: (message: string, metadata?: Record<string, unknown>) => logger.warn('UI', message, metadata),
  error: (message: string, error?: unknown, metadata?: Record<string, unknown>) => logger.error('UI', message, error, metadata),
};

// ✅ SPECS OPTIMIZATION OBSERVABILITY: Enhanced logging for Phase 2 & 3
// Note: specsLogger already defined above, but we keep these specialized methods
// for backward compatibility. The new methods below extend the existing specsLogger.
//
// Phase 2: Context menu positioning tracking
// Phase 3: Duplicate spec operation tracking
//
// Usage:
// - specsLogger.menuPositioned({ pinCenter, menu, strategy, distance })
// - specsLogger.specDuplicated({ originalSpecId, originalCode, newSpecId, newCode, duration })